<template>
  <div class="task-detail-header d-flex flex-column gap-2" data-testid="task-detail-header">
    <div class="task-detail-header__top-row d-flex align-center justify-space-between gap-2" data-testid="task-detail-header__top-row">
      <div class="task-detail-header__meta d-flex align-center flex-wrap gap-2" data-testid="task-detail-header__meta">
        <v-chip
          class="task-detail-header__ref-chip"
          data-testid="task-detail-header__ref-chip"
        >
          <span>{{ task.ref || 'Pending' }}</span>
        </v-chip>

        <StatusIndicator
          :variant="statusVariant"
          :custom-label="statusLabel"
          size="sm"
          outlined
          data-testid="task-detail-header__status"
        />

        <template v-if="isAccountRecon">
          <v-chip
            v-if="isReconValidationSkipped"
            class="validation-skip"
            data-testid="task-detail-header__validation-skipped"
          >
            <span>Validation Skipped</span>
          </v-chip>
          <v-chip
            v-else
            :class="matchFlag ? 'matched-chip' : 'unmatched-chip'"
            data-testid="task-detail-header__match-flag"
          >
            <span>{{ matchFlag ? 'matched' : 'unmatched' }}</span>
          </v-chip>
        </template>
      </div>

      <div class="task-detail-header__actions d-flex align-center flex-wrap justify-end gap-2" data-testid="task-detail-header__actions">
      <!-- Preparer actions (left among role actions) -->
      <template v-if="capabilities.can_submit">
        <v-btn
          class="secondaryBtn text-capitalize"
          data-testid="task-detail-header__upload-btn"
          @click="$emit('open-upload-drawer', 'task-header')"
        >
          <v-icon left small data-testid="task-detail-header__upload-icon">mdi-upload</v-icon>
          <span>Upload</span>
        </v-btn>
        <v-btn
          class="primaryBtn text-capitalize"
          :loading="submitLoading"
          data-testid="task-detail-header__submit-btn"
          @click="handleSubmitTask"
        >
          <v-icon left small data-testid="task-detail-header__submit-icon">mdi-check-circle-outline</v-icon>
          <span>Submit</span>
        </v-btn>
      </template>

      <!-- Approver actions -->
      <template v-if="capabilities.can_l1_approve || capabilities.can_sign_off || capabilities.can_reject || capabilities.can_return_to_pending">
        <FcTooltip
          variant="rich"
          eager
          transition="fade-transition"
          :disabled="!showRejectApprovalTooltip"
          activator-class="task-detail-header__action-tooltip-activator"
          data-testid="task-detail-header__approval-blocked-tooltip"
        >
          <v-menu
            v-if="showRejectButton"
            v-model="rejectMenu"
            content-class="task-detail-reason-menu-popover"
            :close-on-content-click="false"
            offset-y
            nudge-bottom="4"
            left
            :disabled="isApprovalBlockedByDependencies || isResubmissionBlockedByApprovedTasks"
          >
            <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
              <v-btn
                class="gap-2 text-capitalize"
                :class="[
                  isRequestResubmission ? 'secondaryBtn' : 'task-detail-header__reject-btn',
                  {
                    'task-detail-header__reject-btn--blocked': isRejectDisabled && !isRequestResubmission,
                    'task-detail-header__action-btn--disabled': isRejectDisabled,
                  },
                ]"
                height="32"
                :outlined="!isRequestResubmission"
                :disabled="isRejectDisabled"
                v-bind="{ ...menuAttrs }"
                v-on="menuOn"
                data-testid="task-detail-header__reject-btn"
              >
                <img
                  v-if="!isRequestResubmission"
                  src="/img/icons/fbc-icons/RejectIcon.svg"
                  alt=""
                  class="task-detail-header__reject-btn-icon"
                />
                <span>{{ rejectButtonLabel }}</span>
              </v-btn>
            </template>
            <TaskDetailReasonMenu
              v-bind="rejectReasonMenuConfig"
              v-model="rejectRemark"
              data-testid="task-detail-header__reject-menu"
              reason-input-testid="task-detail-header__reject-reason-input"
              confirm-testid="task-detail-header__reject-confirm-btn"
              @confirm="confirmReject"
            />
          </v-menu>
          <template #content>
            <template v-if="showResubmissionBlockedTooltip">
              <p class="fc-tooltip-rich__title">
                {{ dependencyCopy.RESUBMISSION_UNAVAILABLE_TITLE }}
              </p>
              <p class="fc-tooltip-rich__subtitle">
                {{ resubmissionBlockedByLabel }}
              </p>
              <p
                v-for="(row, index) in approvedBlockingTasks"
                :key="`resubmission-blocked-${rowId(row)}-${index}`"
                class="fc-tooltip-rich__line"
                :data-testid="`task-detail-header__resubmission-blocked-task--${index}`"
              >
                {{ blockedByLine(row) }}
              </p>
              <div class="fc-tooltip-rich__divider" />
              <p class="fc-tooltip-rich__footer">
                {{ dependencyCopy.RESUBMISSION_BLOCKED_FOOTER }}
              </p>
            </template>
            <template v-else-if="showApprovalBlockedTooltip">
              <p class="fc-tooltip-rich__title">
                {{ rejectBlockedTitle }}
              </p>
              <p class="fc-tooltip-rich__subtitle">
                {{ approvalBlockedByLabel }}
              </p>
              <p
              v-for="(row, index) in unresolvedBlockedByTasks"
              :key="`approval-blocked-${rowId(row)}-${index}`"
                class="fc-tooltip-rich__line"
                :data-testid="`task-detail-header__approval-blocked-task--${index}`"
              >
                {{ blockedByLine(row) }}
              </p>
              <div class="fc-tooltip-rich__divider" />
              <p class="fc-tooltip-rich__footer">
                {{ approvalBlockedUnlockMessage }}
              </p>
            </template>
            <span v-else-if="task.stage === 0">Task must be submitted before rejection</span>
          </template>
        </FcTooltip>

        <FcTooltip
          v-if="capabilities.can_l1_approve"
          variant="rich"
          eager
          transition="fade-transition"
          :disabled="!showApprovalBlockedTooltip"
          activator-class="task-detail-header__action-tooltip-activator"
          data-testid="task-detail-header__approve-blocked-tooltip"
        >
          <v-menu
            v-model="approverMenu"
            content-class="task-detail-reason-menu-popover"
            :close-on-content-click="false"
            offset-y
            nudge-bottom="4"
            left
            :disabled="isApprovalBlockedByDependencies"
          >
            <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
              <v-btn
                class="task-detail-header__approve-btn text-capitalize"
                :class="{
                  'task-detail-header__approve-btn--blocked': isApproveDisabled,
                  'task-detail-header__action-btn--disabled': isApproveDisabled,
                }"
                height="32"
                outlined
                v-bind="menuAttrs"
                v-on="menuOn"
                :disabled="isApproveDisabled"
                data-testid="task-detail-header__approve-btn"
              >
                <img
                  src="/img/icons/fbc-icons/SignOffIcon.svg"
                  alt=""
                  class="task-detail-header__approve-btn-icon"
                />
                <span>Approve</span>
              </v-btn>
            </template>
            <TaskDetailReasonMenu
              :title="reasonMenuCopy.APPROVE_TITLE"
              :placeholder="reasonMenuCopy.APPROVE_PLACEHOLDER"
              v-model="approverRemark"
              data-testid="task-detail-header__approve-menu"
              reason-input-testid="task-detail-header__approve-reason-input"
              confirm-testid="task-detail-header__approve-confirm-btn"
              @confirm="confirmApprove"
            />
          </v-menu>
          <template #content>
            <p class="fc-tooltip-rich__title">
              {{ dependencyCopy.APPROVAL_UNAVAILABLE_TITLE }}
            </p>
            <p class="fc-tooltip-rich__subtitle">
              {{ approvalBlockedByLabel }}
            </p>
            <p
              v-for="(row, index) in unresolvedBlockedByTasks"
              :key="`approve-blocked-${rowId(row)}-${index}`"
              class="fc-tooltip-rich__line"
              :data-testid="`task-detail-header__approve-blocked-task--${index}`"
            >
              {{ blockedByLine(row) }}
            </p>
            <div class="fc-tooltip-rich__divider" />
            <p class="fc-tooltip-rich__footer">
              {{ approvalBlockedUnlockMessage }}
            </p>
          </template>
        </FcTooltip>

        <v-menu
          v-if="capabilities.can_sign_off"
          v-model="signOffMenu"
          content-class="task-detail-reason-menu-popover"
          :close-on-content-click="false"
          offset-y
          nudge-bottom="10"
          left
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="task-detail-header__approve-btn"
              height="32"
              outlined
              v-bind="attrs"
              v-on="on"
              data-testid="task-detail-header__sign-off-btn"
            >
              <img
                src="/img/icons/fbc-icons/SignOffIcon.svg"
                alt=""
                class="task-detail-header__sign-off-btn-icon"
              />
              <span>Sign Off</span>
            </v-btn>
          </template>
          <TaskDetailReasonMenu
            :title="reasonMenuCopy.SIGN_OFF_TITLE"
            :placeholder="reasonMenuCopy.SIGN_OFF_PLACEHOLDER"
            v-model="signOffRemark"
            data-testid="task-detail-header__sign-off-menu"
            reason-input-testid="task-detail-header__sign-off-reason-input"
            confirm-testid="task-detail-header__sign-off-confirm-btn"
            @confirm="confirmSignOff"
          />
        </v-menu>
      </template>

      <!-- Manager actions (rightmost) -->
      <template v-if="capabilities.manager">
        <v-tooltip content-class="tooltip-left" left open-delay="500">
          <template v-slot:activator="{ on, attrs }">
            <span
              class="task-detail-header__edit-wrap"
              :class="{ 'task-detail-header__edit-wrap--locked': isEditLocked }"
              v-bind="attrs"
              v-on="on"
            >
              <v-btn
                icon
                class="secondaryBtn edit-btn"
                :disabled="isEditLocked"
                aria-label="Edit task"
                data-testid="task-detail-header__edit-btn"
                @click="onEditClick"
              >
                <img src="/img/icons/PenEditIcon.svg" alt="" />
              </v-btn>
            </span>
          </template>
          <span>{{ editTooltip }}</span>
        </v-tooltip>

        <v-menu
          v-model="moreMenuOpen"
          offset-y
          left
          :close-on-content-click="true"
          content-class="task-detail-header__more-menu-panel"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-tooltip
              content-class="tooltip-left"
              left
              open-delay="500"
              :disabled="moreMenuOpen"
              :open-on-focus="false"
            >
              <template v-slot:activator="{ on: tipOn, attrs: tipAttrs }">
                <v-btn
                  icon
                  class="secondaryBtn edit-btn task-detail-header__more-btn"
                  v-bind="{ ...attrs, ...tipAttrs }"
                  v-on="{ ...on, ...tipOn }"
                  aria-label="More actions"
                  data-testid="task-detail-header__more-btn"
                >
                  <img
                    src="/img/icons/fbc-icons/MoreActionsIcon.svg"
                    alt=""
                    class="task-detail-header__more-btn-icon"
                  />
                </v-btn>
              </template>
              <span>More actions</span>
            </v-tooltip>
          </template>

          <div class="task-detail-header__more-menu" data-testid="task-detail-header__more-menu">
            <button
              v-if="canLinkNoa"
              type="button"
              class="task-detail-header__more-item"
              data-testid="task-detail-header__link-noa"
              @click="$emit('link-noa')"
            >
              <span class="task-detail-header__more-icon-wrap">
                <img
                  src="/img/icons/fbc-icons/LinkNoaAgent.svg"
                  alt=""
                  class="task-detail-header__more-icon-img"
                />
              </span>
              <span class="task-detail-header__more-label">Link Noa Agent</span>
            </button>

            <button
              v-if="canUnlinkNoa"
              type="button"
              class="task-detail-header__more-item"
              data-testid="task-detail-header__unlink-noa"
              @click="$emit('unlink-noa')"
            >
              <span class="task-detail-header__more-icon-wrap">
                <v-icon class="task-detail-header__more-icon">mdi-link-off</v-icon>
              </span>
              <span class="task-detail-header__more-label">Unlink {{ $constants.BOT_NAME }}</span>
            </button>

            <button
              type="button"
              class="task-detail-header__more-item"
              data-testid="task-detail-header__delete"
              @click="$emit('delete')"
            >
              <span class="task-detail-header__more-icon-wrap">
                <img
                  src="/img/icons/fbc-icons/DeleteActions.svg"
                  alt=""
                  class="task-detail-header__more-icon-img"
                />
              </span>
              <span class="task-detail-header__more-label">Delete</span>
            </button>
          </div>
        </v-menu>
      </template>
      </div>
    </div>

    <ExpandableText
      :text="task.description || ''"
      :lines="3"
      class="task-detail-header__title"
      data-testid="task-detail-header__title"
    />

    <!-- Reject / auto-approve resubmission banner hidden for now.
    <TaskAutoApprovedResubmissionBanner
      v-if="showAutoApprovedResubmissionBanner"
      :title="AUTO_APPROVE.RESUBMISSION_BANNER_TITLE"
      :body="autoApprovedResubmissionBannerBody"
    />
    -->
  </div>
</template>

<script>
import StatusIndicator from '@/components/StatusIndicator.vue';
import { ExpandableText } from '@/modules/financialclose/components';
// import TaskAutoApprovedResubmissionBanner from './TaskAutoApprovedResubmissionBanner.vue';
import { FcTooltip } from '@/modules/financialclose/components';
import TaskDetailReasonMenu from './TaskDetailReasonMenu/TaskDetailReasonMenu.vue';
import {
  CHECKLIST_STATUS_ACTION,
  REJECTION_REMARK,
  TASK_EDIT_MESSAGES,
  AUTO_APPROVE,
  RETURN_TO_PENDING,
  CYCLE_TASK_DEPENDENCY,
  TASK_DETAIL_REASON_MENU,
  TASK_STAGES,
} from '@/modules/financialclose/utils/constants';
import { isTaskClosedForActions } from '@/modules/financialclose/utils/taskDetailCapabilities';
import {
  getTaskStageDisplayLabel,
  getTaskStageStatusVariant,
  // getAutoApprovedResubmissionBannerBody,
  // isAutoApprovedResubmission,
  isValidRejectionRemark,
  isApprovedTaskEditLocked,
  shouldUseReturnToPendingAction,
  validateAccountReconTaskSubmission,
  isReconValidationSkipped,
} from '@/modules/financialclose/utils/tasklistHelpers';
import { calculateUnexplainedDifference } from '@/modules/financialclose/utils/utils';

export default {
  name: 'TaskDetailHeader',

  components: {
    StatusIndicator,
    ExpandableText,
    FcTooltip,
    TaskDetailReasonMenu,
    // TaskAutoApprovedResubmissionBanner,
  },

  props: {
    task: {
      type: Object,
      required: true,
    },
    capabilities: {
      type: Object,
      default: () => ({
        preparer: false,
        approver: false,
        manager: false,
        can_submit: false,
        can_l1_approve: false,
        can_sign_off: false,
        can_reject: false,
        can_return_to_pending: false,
        can_edit: false,
      }),
    },
    threshold: {
      type: [Number, String],
      default: 0,
    },
  },

  data() {
    return {
      approverMenu: false,
      approverRemark: '',
      signOffMenu: false,
      signOffRemark: '',
      rejectMenu: false,
      rejectRemark: '',
      moreMenuOpen: false,
      submitLoading: false,
      accountReconType: this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION,
      AUTO_APPROVE,
      dependencyCopy: CYCLE_TASK_DEPENDENCY,
    };
  },

  computed: {
    blockedByTasks() {
      return Array.isArray(this.task?.blockedBy) ? this.task.blockedBy : [];
    },

    /** Neighbors that still block approval (stage !== approved). */
    unresolvedBlockedByTasks() {
      return this.blockedByTasks.filter((row) => Number(row?.stage) !== TASK_STAGES.CLOSED);
    },

    /** Tasks this one blocks that are already approved. */
    approvedBlockingTasks() {
      const rows = Array.isArray(this.task?.blocking) ? this.task.blocking : [];
      return rows.filter((row) => Number(row?.stage) === TASK_STAGES.CLOSED);
    },

    isResubmissionBlockedByApprovedTasks() {
      return this.isRequestResubmission && this.approvedBlockingTasks.length > 0;
    },

    showResubmissionBlockedTooltip() {
      return this.isResubmissionBlockedByApprovedTasks;
    },

    /** getdetails canApprove — false when upstream dependencies are not approved. */
    isApprovalBlockedByDependencies() {
      return this.task?.canApprove === false;
    },

    showApprovalBlockedTooltip() {
      return this.isApprovalBlockedByDependencies && this.unresolvedBlockedByTasks.length > 0;
    },

    isRejectDisabled() {
      if (this.isRequestResubmission) {
        return this.isApprovalBlockedByDependencies || this.isResubmissionBlockedByApprovedTasks;
      }
      return this.task?.stage === TASK_STAGES.PENDING_SUBMISSION || this.isApprovalBlockedByDependencies;
    },

    isApproveDisabled() {
      return this.task?.stage === 0 || this.isApprovalBlockedByDependencies;
    },

    showRejectApprovalTooltip() {
      if (this.isRequestResubmission) {
        return this.showApprovalBlockedTooltip || this.showResubmissionBlockedTooltip;
      }
      return this.showApprovalBlockedTooltip || this.task?.stage === TASK_STAGES.PENDING_SUBMISSION;
    },

    resubmissionBlockedByLabel() {
      const count = this.approvedBlockingTasks.length;
      if (count === 1) return CYCLE_TASK_DEPENDENCY.RESUBMISSION_BLOCKED_BY_ONE;
      return CYCLE_TASK_DEPENDENCY.RESUBMISSION_BLOCKED_BY_MANY.replace('{count}', String(count));
    },

    resubmissionBlockedMessage() {
      const refs = this.approvedBlockingTasks.map((row) => String(row?.ref || '').trim()).filter(Boolean);
      return CYCLE_TASK_DEPENDENCY.resubmissionBlockedMessage(refs);
    },

    rejectBlockedTitle() {
      return this.isRequestResubmission
        ? CYCLE_TASK_DEPENDENCY.RESUBMISSION_UNAVAILABLE_TITLE
        : CYCLE_TASK_DEPENDENCY.REJECT_UNAVAILABLE_TITLE;
    },

    approvalBlockedByLabel() {
      const count = this.unresolvedBlockedByTasks.length;
      if (count === 1) return CYCLE_TASK_DEPENDENCY.APPROVAL_BLOCKED_BY_ONE;
      return CYCLE_TASK_DEPENDENCY.APPROVAL_BLOCKED_BY_MANY.replace('{count}', String(count));
    },

    approvalBlockedUnlockMessage() {
      const count = this.unresolvedBlockedByTasks.length;
      if (count === 1) return CYCLE_TASK_DEPENDENCY.APPROVAL_UNLOCKS_ONCE_ONE;
      if (count === 2) return CYCLE_TASK_DEPENDENCY.APPROVAL_UNLOCKS_ONCE_BOTH;
      return CYCLE_TASK_DEPENDENCY.APPROVAL_UNLOCKS_ONCE_ALL;
    },

    showRejectButton() {
      const caps = this.capabilities || {};
      return caps.can_reject || caps.can_l1_approve || caps.can_sign_off || caps.can_return_to_pending;
    },

    isRequestResubmission() {
      return shouldUseReturnToPendingAction({ capabilities: this.capabilities });
    },

    rejectButtonLabel() {
      return this.isRequestResubmission ? RETURN_TO_PENDING.LABEL : 'Reject';
    },

    reasonMenuCopy() {
      return TASK_DETAIL_REASON_MENU;
    },

    rejectReasonMenuConfig() {
      if (this.isRequestResubmission) {
        return {
          title: TASK_DETAIL_REASON_MENU.RESUBMISSION_TITLE,
          placeholder: TASK_DETAIL_REASON_MENU.RESUBMISSION_PLACEHOLDER,
          remarksRequired: true,
        };
      }

      return {
        title: TASK_DETAIL_REASON_MENU.REJECT_TITLE,
        placeholder: TASK_DETAIL_REASON_MENU.REJECT_PLACEHOLDER,
        remarksRequired: true,
      };
    },

    isAccountRecon() {
      return this.task?.task_type === this.accountReconType;
    },

    isReconValidationSkipped() {
      return isReconValidationSkipped(this.task);
    },

    statusVariant() {
      return getTaskStageStatusVariant(this.task);
    },

    statusLabel() {
      return getTaskStageDisplayLabel(this.task) || this.task?.display_status_label || this.task?.statusBadge || '';
    },

    // Reject banner hidden for now.
    // showAutoApprovedResubmissionBanner() {
    //   return isAutoApprovedResubmission(this.task);
    // },

    userMap() {
      return this.$store.getters['tasks/getUserMap'] || {};
    },

    reopenActorName() {
      const raw = this.task?.approval_state?.last_action_by;
      if (!raw) return '';
      const id = typeof raw === 'object'
        ? String(raw._id || raw.$oid || '')
        : String(raw);
      if (!id) return '';
      const name = (this.userMap[id]?.name || '').trim();
      return name ? name.split(/\s+/)[0] : '';
    },

    reopenDateLabel() {
      const at = this.task?.approval_state?.last_action_at;
      if (!at || !this.$nova?.formatDateTime) return '';
      return this.$nova.formatDateTime(at, 'DD MMM YYYY') || '';
    },

    // autoApprovedResubmissionBannerBody() {
    //   return getAutoApprovedResubmissionBannerBody({
    //     actorName: this.reopenActorName,
    //     dateLabel: this.reopenDateLabel,
    //   });
    // },

    isClosedTask() {
      return isTaskClosedForActions(this.task);
    },

    matchFlag() {
      const unexplained = Number(calculateUnexplainedDifference(this.task));
      const thresh = Number(this.threshold);
      if (Number.isNaN(thresh)) return false;
      return Math.abs(unexplained) <= thresh;
    },

    canLinkNoa() {
      return this.isAccountRecon && !this.task?.botid && !this.task?.bot_details?._id;
    },

    canUnlinkNoa() {
      return !!(this.task?.botid || this.task?.bot_details?._id);
    },

    isEditLocked() {
      return isApprovedTaskEditLocked(this.task);
    },

    editTooltip() {
      return this.isEditLocked ? TASK_EDIT_MESSAGES.APPROVED_LOCKED : 'Edit';
    },
  },

  methods: {
    rowId(row) {
      const id = row?._id;
      if (id == null) return '';
      return typeof id === 'object' ? String(id.$oid || id._id || '') : String(id);
    },

    blockedByLine(row) {
      const ref = String(row?.ref || '').trim();
      const name = String(row?.description || 'Task').trim();
      return ref ? `${ref} · ${name}` : name;
    },

    onEditClick() {
      if (this.isEditLocked) return;
      this.$emit('edit');
    },

    async handleSubmitTask() {
      if (this.submitLoading) return;
      const validationError = validateAccountReconTaskSubmission(this.task);
      if (validationError) {
        this.$store.commit('sbError', validationError);
        return;
      }

      this.submitLoading = true;
      try {
        const cycleId = this.task.project;
        const taskId = this.task._id;
        if (!cycleId || !taskId) throw new Error('Missing cycle or task ID');
        const response = await this.$store.dispatch('tasks/submitTask', { taskId, cycleId });
        if (response?.status === 'success') {
          this.$emit('task-submitted', response);
          this.$emit('refresh');
        }
      } finally {
        this.submitLoading = false;
      }
    },

    confirmApprove() {
      this.$emit('change-status', {
        id: this.task._id,
        status: CHECKLIST_STATUS_ACTION.APPROVE,
        remark: this.approverRemark || '',
      });
      this.approverMenu = false;
      this.approverRemark = '';
    },

    confirmReject() {
      if (this.isResubmissionBlockedByApprovedTasks) {
        this.$store.commit('sbError', this.resubmissionBlockedMessage);
        return;
      }
      const remark = (this.rejectRemark || '').trim();
      if (!remark) {
        this.$store.commit('sbError', REJECTION_REMARK.REQUIRED);
        return;
      }
      if (!isValidRejectionRemark(remark)) {
        this.$store.commit('sbError', REJECTION_REMARK.INVALID);
        return;
      }

      this.$emit('change-status', {
        id: this.task._id,
        status: this.isRequestResubmission
          ? CHECKLIST_STATUS_ACTION.RETURN_TO_PENDING
          : CHECKLIST_STATUS_ACTION.REJECT,
        remark,
      });
      this.rejectMenu = false;
      this.rejectRemark = '';
    },

    confirmSignOff() {
      this.$emit('change-status', {
        id: this.task._id,
        status: CHECKLIST_STATUS_ACTION.SIGNOFF,
        remark: this.signOffRemark || '',
      });
      this.signOffMenu = false;
      this.signOffRemark = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.task-detail-header {
  padding: var(--space-2) 0;
  gap: var(--space-xs);
}

.task-detail-header__top-row {
  width: 100%;
  align-items: flex-start;
}

.task-detail-header__meta {
  flex: 1;
  min-width: 0;
  gap: var(--space-xs);
}

.task-detail-header__actions {
  flex-shrink: 0;
  gap: var(--space-xs);
}

.task-detail-header__ref-chip {
  border-radius: var(--radius-sm);
  background: rgba(218, 218, 218, 0.4) !important;
  padding: var(--space-1) var(--space-xs);
  height: 22px;
}

.task-detail-header__ref-chip ::v-deep .v-chip__content {
  color: var(--slate-500);
  font-size: var(--font-size-xxs);
  font-weight: var(--font-weight-semibold);
  line-height: 19.5px;
}

.gap-2 {
  gap: var(--space-xs);
}

.validation-skip {
  border-radius: var(--radius-12);
  background: var(--yellow-50, #FAF7F2) !important;
  padding: var(--space-1) var(--space-xs-5) var(--space-1) var(--space-xs);
  height: var(--icon-md);
}

.validation-skip ::v-deep .v-chip__content {
  color: var(--amber-default, #EBA937)
}

.unmatched-chip {
  border-radius: var(--radius-12);
  background: var(--red-50, #FDF4F4) !important;
  padding: var(--space-1) var(--space-xs-5) var(--space-1) var(--space-xs);
  height: var(--icon-md);
}

.unmatched-chip ::v-deep .v-chip__content {
  color: var(--red-700, #C64E53);
  text-transform: capitalize;
}

.matched-chip {
  border-radius: var(--radius-12);
  border: var(--border-sm) solid var(--green-100, #e8f2ec) !important;
  background: var(--green-50, #F3F8F5) !important;
  padding: var(--space-1) var(--space-xs-5) var(--space-1) var(--space-xs);
  height: var(--icon-md);
}

.matched-chip ::v-deep .v-chip__content {
  color: var(--green-500, #37AE78);
  text-transform: capitalize;
}

.edit-btn {
  border: var(--border-sm) solid var(--color-border-default, #e5e7eb) !important;
  width: var(--size-control-sm) !important;
  height: var(--size-control-sm) !important;
  border-radius: var(--radius-sm) !important;
}

.task-detail-header__edit-wrap {
  display: inline-flex;

  &--locked,
  &--locked ::v-deep .v-btn {
    cursor: not-allowed;
  }
}

.task-detail-header__more-btn-icon {
  height: var(--icon-xs);
  object-fit: contain;
  width: var(--icon-xs);
}

.task-detail-header__upload-btn {
  border-color: var(--color-primary-base, #193541) !important;
  color: var(--color-primary-base, #193541) !important;
  text-transform: capitalize;
}

.task-detail-header__submit-btn {
  text-transform: capitalize;
}

::v-deep .task-detail-header__action-tooltip-activator {
  display: inline-flex;
  width: max-content;
  max-width: max-content;

  .v-menu {
    display: inline-flex;
    width: max-content;
  }
}

.task-detail-header__action-btn--disabled {
  pointer-events: none;
}

.task-detail-header__reject-btn {
  border: var(--border-sm, 1px) solid var(--red-300, #ff4d4d) !important;
  color: var(--grey-700, #4d575b) !important;
  background: var(--grey-white, #fff) !important;
  box-shadow: none !important;
  font-size: var(--font-size-xs, 14px) !important;
  font-weight: var(--font-weight-medium, 500) !important;
  letter-spacing: 0;
  line-height: 20px !important;
  min-width: 0 !important;
  padding: 0 var(--padding-sm, 12px) !important;
  text-transform: none !important;

  ::v-deep .v-btn__content {
    gap: var(--padding-2xs, 6px);
  }

  &.v-btn--disabled,
  &--blocked {
    opacity: 0.5 !important;
    border-color: var(--red-300, #ff4d4d) !important;
    color: var(--grey-700, #4d575b) !important;
    background: var(--grey-white, #fff) !important;
  }
}

.task-detail-header__reject-btn-icon {
  width: var(--icon-xs, 16px);
  height: var(--icon-xs, 16px);
  flex-shrink: 0;
}

.task-detail-header__approve-btn {
  border: var(--border-sm, 1px) solid var(--status-green, #38b000) !important;
  color: var(--grey-700, #4d575b) !important;
  background: var(--grey-white, #fff) !important;
  box-shadow: none !important;
  font-size: var(--font-size-xs, 14px) !important;
  font-weight: var(--font-weight-medium, 500) !important;
  letter-spacing: 0;
  line-height: 20px !important;
  min-width: 0 !important;
  padding: 0 var(--padding-sm, 12px) !important;
  text-transform: none !important;

  ::v-deep .v-btn__content {
    gap: var(--padding-2xs, 6px);
  }

  &.v-btn--disabled,
  &--blocked {
    opacity: 0.5 !important;
    border-color: var(--status-green, #38b000) !important;
    color: var(--grey-700, #4d575b) !important;
    background: var(--grey-white, #fff) !important;
  }
}

.task-detail-header__approve-btn-icon,
.task-detail-header__sign-off-btn-icon {
  width: var(--icon-xs, 16px);
  height: var(--icon-xs, 16px);
  flex-shrink: 0;
}

::v-deep .task-detail-header__title .expandable-text__content {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold, 600);
  line-height: 22px;
  color: var(--grey-900, #2c2c35);
}
</style>

<style lang="scss">
@import './TaskDetailHeader-more-menu.scss';
</style>
